import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { Box, Button, Typography } from "@material-ui/core";

/**
 * Canvas signature pad. Use ref.getDataUrl() / clear() — avoids wiping the canvas on parent re-renders.
 */
const SignatureCapture = forwardRef(function SignatureCapture(
  { disabled, height = 160, label, initialDataUrl },
  ref
) {
  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);
  const last = React.useRef({ x: 0, y: 0 });

  const getCtx = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    return { canvas, ctx };
  };

  const layoutCanvas = React.useCallback(() => {
    const { canvas, ctx } = getCtx() || {};
    if (!canvas || !ctx) return;
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const w = canvas.offsetWidth || 320;
    const h = height;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [height]);

  useEffect(() => {
    layoutCanvas();
    if (initialDataUrl && String(initialDataUrl).startsWith("data:image/png")) {
      const { canvas, ctx } = getCtx() || {};
      if (!canvas || !ctx) return;
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.offsetWidth || 320, height);
      };
      img.src = initialDataUrl;
    }
  }, [height, initialDataUrl, layoutCanvas]);

  useImperativeHandle(ref, () => ({
    getDataUrl: () => {
      const canvas = canvasRef.current;
      if (!canvas) return "";
      try {
        return canvas.toDataURL("image/png");
      } catch {
        return "";
      }
    },
    clear: () => {
      layoutCanvas();
    },
  }));

  const pos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const drawLine = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const { ctx } = getCtx() || {};
    if (!ctx) return;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const end = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
  };

  const clear = () => {
    layoutCanvas();
  };

  return (
    <Box>
      {label ? (
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          {label}
        </Typography>
      ) : null}
      <Box
        style={{
          border: "1px solid #cbd5e1",
          borderRadius: 8,
          overflow: "hidden",
          background: "#fff",
          touchAction: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height, display: "block", cursor: disabled ? "not-allowed" : "crosshair" }}
          onMouseDown={start}
          onMouseMove={drawLine}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={drawLine}
          onTouchEnd={end}
        />
      </Box>
      <Box mt={1} display="flex" justifyContent="flex-end">
        <Button size="small" onClick={clear} disabled={disabled}>
          Clear
        </Button>
      </Box>
    </Box>
  );
});

export default SignatureCapture;
